import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, DollarSign, Plus, Trash2,
  Check, FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { finesAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

export default function GenerateFinePage() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fine configuration state
  const [selectedViolations, setSelectedViolations] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await finesAPI.getFineReport(scanId);
        setReport(res.data);

        // Auto-select all violations that have fine schedules
        const initial = [];
        (res.data.violations || []).forEach((v, vi) => {
          if (v.fine_schedule_configured) {
            (v.fine_entries || []).forEach((fe, fi) => {
              initial.push({
                key: `${vi}-${fi}`,
                law_id: v.law_id,
                law_title: v.law_title,
                violation_description: fe.violation_description,
                fine_amount: fe.min_amount,
                min_amount: fe.min_amount,
                max_amount: fe.max_amount,
                frequency: fe.frequency,
                notes: '',
                selected: false,
              });
            });
          }
        });
        setSelectedViolations(initial);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load fine report');
        navigate('/fines');
      }
      setLoading(false);
    };
    fetch();
  }, [scanId, navigate]);

  // [DEF-F8 Step A] REMOVED: `toggleViolation` / `updateViolation` /
  // `getSelected` / `totalFine` / `handleSubmit`. Together these selected
  // violations, set per-violation dollar amounts, summed them into an aggregate,
  // and POSTed the result to `finesAPI.create` — the fine-ticket creation path.
  // Counsel ruling 2026-07-30: informational/referential only, no issuance and no
  // notification. The client no longer has any code path that creates a fine;
  // the server refuses it independently (FINE_ISSUANCE_ENABLED). Violations are
  // now rendered as read-only reference information.

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!report) return null;

  // Phase 8 TODO: When Procurement role scans third-party domains, the scan document
  // will include a scanned_entity_id field that should be used as the target_entity
  // instead of requested_by_user_id.
  const scannedBy = report.scanned_by;
  const targetEntity = report.target_entity;
  const scan = report.scan;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="generate-fine-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Generate Fine Report</h1>
          <p className="text-sm text-muted-foreground">Based on scan results for {scan?.target_url}</p>
        </div>
      </div>

      {/* Entity Info */}
      <Card className="border-border/60">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div><span className="text-xs text-muted-foreground">Target Entity</span><p className="font-medium">{targetEntity?.entity_name || targetEntity?.name || '\u2014'}</p></div>
            <div><span className="text-xs text-muted-foreground">Entity Email</span><p className="font-medium">{targetEntity?.email || '\u2014'}</p></div>
            <div><span className="text-xs text-muted-foreground">Scanned By</span><p className="font-medium">{scannedBy?.name || scannedBy?.email || '\u2014'}</p></div>
            <div><span className="text-xs text-muted-foreground">URL Scanned</span><p className="font-medium">{scan?.target_url || '\u2014'}</p></div>
            <div><span className="text-xs text-muted-foreground">Non-Compliant Laws</span><p className="font-bold text-vrs-red">{report.total_laws_non_compliant}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* [Phase 3(c) Stage 1, §6] Ley 40-2024 dual-reading penalty banner —
          rendered only when the report covers Ley 40; text sourced from
          db.law_meta (seeded crosswalk), never hardcoded. */}
      {report.ley40_penalty_notice && (
        <Card className="border-vrs-amber/40 bg-vrs-amber/5" data-testid="ley40-penalty-banner">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5 shrink-0" />
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">{report.ley40_penalty_notice.heading}</p>
                {report.ley40_penalty_notice.intro && (
                  <p className="text-xs text-muted-foreground leading-relaxed">{report.ley40_penalty_notice.intro}</p>
                )}
                {report.ley40_penalty_notice.dual_reading && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Provider-sanction dual reading (Art. 10 ¶¶3-4)</p>
                    <p className="text-[11px] text-foreground/90 leading-relaxed">{report.ley40_penalty_notice.dual_reading}</p>
                  </div>
                )}
                {report.ley40_penalty_notice.scrivener_caveat && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Daily-fine enforceability (finding 6.A)</p>
                    <p className="text-[11px] text-foreground/90 leading-relaxed">{report.ley40_penalty_notice.scrivener_caveat}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Violations Selection */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Select Violations</h2>

        {(report.violations || []).map((v, vi) => (
          <Card key={vi} className={cn('border-border/60', !v.fine_schedule_configured && 'opacity-60')}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-semibold">{v.law_title}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-[10px]">{v.categories ? v.categories.join(', ') : v.category}</Badge>
                    <Badge variant="outline" className={cn('text-[10px]', v.compliance_score >= 80 ? 'text-vrs-green border-vrs-green/30' : v.compliance_score >= 60 ? 'text-vrs-amber border-vrs-amber/30' : 'text-vrs-red border-vrs-red/30')}>
                      {v.compliance_score}%
                    </Badge>
                  </div>
                </div>
                {!v.fine_schedule_configured && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">Fine schedule not configured</Badge>
                )}
              </div>
            </CardHeader>
            {v.fine_schedule_configured && (
              <CardContent className="space-y-3">
                {selectedViolations.filter(sv => sv.law_id === v.law_id).map(sv => (
                  <div key={sv.key} className="rounded-lg border border-border/40 px-4 py-3">
                    <div className="flex items-start gap-3">
                      {/* [DEF-F8 Step A] Selection checkbox removed — its only
                          consumer was the fine-ticket submit payload. */}
                      {/* [DEF-F8 Step A] Issuance affordances removed: the editable
                          Fine Amount input, its min/max range caption, and the
                          per-violation notes field were inputs to creating a fine
                          TICKET. This tool is informational only — it does not
                          issue fines or assess amounts. The statutory frequency is
                          retained as reference information. Step B replaces this
                          block with the seeded penalty-matrix prose sanctions. */}
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium">{sv.violation_description}</p>
                        <div>
                          <Label className="text-[10px]">Statutory frequency</Label>
                          <p className="text-xs text-muted-foreground capitalize">{sv.frequency.replace('_', ' ')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* [DEF-F8 Step A] Issuance block removed — the Due Date input, the red
          "Total Fine Amount" aggregate, and the Save-as-Draft / Issue-Fine-Ticket
          buttons created and sent an enforcement instrument. Counsel ruling
          2026-07-30: this subsystem is informational/referential only; there is
          no issuance and no notification. Replaced by an explicit statement of
          what this page is and is not, so its absence is explained rather than
          silently missing. */}
      <Card className="border-border/60" data-testid="fine-informational-notice">
        <CardContent className="p-5 space-y-2">
          <p className="text-sm font-semibold text-foreground">Informational report — no fine is issued</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This report is provided for information and reference only. This tool does not issue fines,
            assess penalty amounts, determine liability, or notify any party. Statutory penalty
            information is reproduced from the sources cited in each entry. Actual penalties are set by
            the applicable regulator or court. Informational only, not legal advice; no attorney-client
            relationship is created.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
